import { ReactNode, useEffect, useLayoutEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { fallbackLanguageIfUnavailable } from "src/i18n/config";
import { makeStyles } from "tss-react/mui";

import { getAvailableLanguages, loadSettings } from "@services/configService";

const useStyles = makeStyles()({
    errorScreen: {
        padding: 24,
        fontFamily: "sans-serif",
    },
});

type Props = { children: ReactNode };

// index.html paints #app-splash before the bundle is even downloaded, so the user sees a spinner
// instead of a blank page while the bundle parses and /config.json is fetched. It covers the
// viewport, so it has to go the moment we have something to render in its place. useLayoutEffect
// (not useEffect) so the removal lands in the same frame as the content and nothing flashes.
const removeAppSplash = () => document.getElementById("app-splash")?.remove();

export const ConfigProvider = ({ children }: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const [ready, setReady] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useLayoutEffect(() => {
        if (ready || error) {
            removeAppSplash();
        }
    }, [ready, error]);

    useEffect(() => {
        loadSettings()
            .then(() => fallbackLanguageIfUnavailable(getAvailableLanguages()))
            .then(() => setReady(true))
            .catch((err: unknown) => {
                setError(err instanceof Error ? err : new Error(String(err)));
            });
    }, []);

    if (error) {
        return (
            <div role="alert" className={classes.errorScreen}>
                <h1>{t("error.configUnavailable.title")}</h1>
                <p>
                    <Trans i18nKey="error.configUnavailable.message" components={{ code: <code /> }} />
                </p>
                <p>{error.message}</p>
            </div>
        );
    }

    if (!ready) {
        return null;
    }

    return <>{children}</>;
};
